// Sprint 6 (Phase 5, D44) — Diff two TodoWrite emits → list of pills.
//
// Between successive TodoWrite emits we emit small inline pills in the chat
// stream so the founder can see which item flipped status without
// scanning the full checklist. Pills are render-time only (no persistence).
//
// Diff rules (ordered, stable):
//   • An item that didn't exist before AND is `in_progress` now → "Started".
//   • Item flips `pending`|`in_progress` → `completed` → "Completed".
//   • Item flips → `cancelled` → "Cancelled".
//   • Pure adds in `pending` state → no pill (low signal).
//   • Removes → no pill (rare; cancelled is the documented path).
//   • No-op (identical lists) → no pills.

import type { Todo } from './derive-progress';

export type TodoUpdateKind = 'started' | 'completed' | 'cancelled';

export interface TodoUpdatePill {
  kind: TodoUpdateKind;
  /** Headline = activeForm if present (for `started`), else content. */
  headline: string;
  /** Stable key for React reconciliation. */
  key: string;
}

export function diffTodos(prev: Todo[], next: Todo[]): TodoUpdatePill[] {
  const prevByContent = new Map<string, Todo>();
  for (const t of prev) prevByContent.set(t.content, t);
  const out: TodoUpdatePill[] = [];
  for (const cur of next) {
    const before = prevByContent.get(cur.content);
    if (!before) {
      // New item.
      if (cur.status === 'in_progress') {
        out.push({
          kind: 'started',
          headline: headline(cur, 'started'),
          key: keyFor(cur, 'started'),
        });
      }
      continue;
    }
    if (before.status === cur.status) continue;
    if (cur.status === 'completed') {
      out.push({
        kind: 'completed',
        headline: headline(cur, 'completed'),
        key: keyFor(cur, 'completed'),
      });
    } else if (cur.status === 'cancelled') {
      out.push({
        kind: 'cancelled',
        headline: headline(cur, 'cancelled'),
        key: keyFor(cur, 'cancelled'),
      });
    } else if (cur.status === 'in_progress' && before.status !== 'in_progress') {
      out.push({
        kind: 'started',
        headline: headline(cur, 'started'),
        key: keyFor(cur, 'started'),
      });
    }
  }
  return out;
}

function headline(t: Todo, kind: TodoUpdateKind): string {
  if (kind === 'started') return t.activeForm ?? t.content;
  return t.content;
}

function keyFor(t: Todo, kind: TodoUpdateKind): string {
  return `${kind}:${t.content}`;
}
